import { CommerceNoticeDoc, CommerceNoticeStatusCode } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button, Popconfirm, Select, Space, message } from 'antd';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { commerceNoticeStatusLabel } from 'src/constants/commerce-notice.constants';

import { formatDateForAgCell } from 'src/utils/ag-grid-util';
import { formatDate } from 'src/utils/date-util';
import { deleteCommerceNotice, updateCommerceNotice } from 'src/utils/firebase/firebase-short-cut';

import styles from './CommerceNoticeTable.module.scss';

interface CommerceNoticeTableProps {
  notices: CommerceNoticeDoc[];
}

const statusOptions = Object.entries(commerceNoticeStatusLabel).map(([value, label]) => ({
  label,
  value: Number(value) as CommerceNoticeStatusCode,
}));

const handleStatusChange = async (noticeId: string, status: CommerceNoticeStatusCode) => {
  try {
    await updateCommerceNotice(noticeId, {
      status,
      ...(status === CommerceNoticeStatusCode.PUBLISHED ? { publishedAt: new Date().toISOString() } : {}),
    });
    message.success(`상태가 '${commerceNoticeStatusLabel[status]}'(으)로 변경되었습니다.`);
  } catch (error) {
    console.error('Status update failed:', error);
    message.error('상태 변경에 실패했습니다.');
  }
};

const handleDelete = async (noticeId: string) => {
  try {
    await deleteCommerceNotice(noticeId);
    message.success('공지사항이 삭제되었습니다.');
  } catch (error) {
    console.error('Delete failed:', error);
    message.error('삭제에 실패했습니다.');
  }
};

const CommerceNoticeTable: FC<CommerceNoticeTableProps> = ({ notices }) => {
  const navigate = useNavigate();

  const defaultColDef: ColDef<CommerceNoticeDoc> = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    []
  );

  const columnDefs: ColDef<CommerceNoticeDoc>[] = useMemo(
    () => [
      {
        headerName: '게시일',
        field: 'publishedAt',
        width: 180,
        valueFormatter: (params) => (params.value ? formatDate(params.value, 'yyyy-MM-dd HH:mm:ss') : ''),
      },
      {
        headerName: '상태',
        field: 'status',
        width: 140,
        filter: false,
        cellRenderer: (params: ICellRendererParams<CommerceNoticeDoc, CommerceNoticeStatusCode>) => {
          if (!params.data) return null;
          const { _id } = params.data;
          return (
            <Select
              size='small'
              value={params.value}
              options={statusOptions}
              onChange={(value) => handleStatusChange(_id, value)}
              style={{ width: '100%' }}
              onClick={(e) => e.stopPropagation()}
              optionRender={(option) => (
                <span style={{ color: option.value === CommerceNoticeStatusCode.PUBLISHED ? '#52c41a' : undefined }}>
                  {option.label}
                </span>
              )}
            />
          );
        },
      },
      {
        headerName: '카테고리',
        field: 'category',
        width: 120,
      },
      {
        headerName: '제목',
        field: 'title',
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: '작성자',
        field: 'authorName',
        width: 120,
      },
      {
        headerName: '상단 고정',
        field: 'pinned',
        width: 120,
        cellRenderer: (params: ICellRendererParams<CommerceNoticeDoc, boolean>) => (params.value ? '📌' : ''),
      },
      {
        headerName: '조회수',
        field: 'viewCount',
        width: 90,
      },
      {
        headerName: '생성일',
        field: '_timeCreate',
        width: 160,
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
      },
      {
        headerName: '',
        field: '_id',
        width: 200,
        filter: false,
        sortable: false,
        resizable: false,
        cellRenderer: (params: ICellRendererParams<CommerceNoticeDoc>) => {
          if (!params.data) return null;
          const id = params.data._id;
          return (
            <Space size={4}>
              <Button size='small' onClick={() => navigate(`/commerce-notice/detail/${id}`)}>
                상세
              </Button>
              <Button size='small' onClick={() => navigate(`/commerce-notice/edit/${id}`)}>
                수정
              </Button>
              <Popconfirm
                title='정말 삭제하시겠습니까?'
                onConfirm={() => handleDelete(id)}
                okText='삭제'
                cancelText='취소'>
                <Button size='small' danger>
                  삭제
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className={`ag-theme-alpine ${styles.tableWrapper}`}>
      <AgGridReact<CommerceNoticeDoc>
        columnDefs={columnDefs}
        rowData={notices}
        defaultColDef={defaultColDef}
        domLayout='autoHeight'
        pagination
        paginationPageSize={20}
      />
    </div>
  );
};

export default CommerceNoticeTable;
